import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';
import axiosInstance, { getErrorMessage } from '../../../utils/axios';
import CRUD from '../../../utils/Crud';
import { fDateTime } from '../../../utils/formatTime';
import { formatWalletAmount } from './utils';
import { EmptyModuleState, SectionError } from './ManageUserDialogStates';
import TransactionSideDrawer from '../TransactionSideDrawer';

function getStatusColor(status) {
  switch (status) {
    case 'successful':
    case 'completed':
    case 'verified':
      return 'success';
    case 'failed':
    case 'rejected':
    case 'cancelled':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

function getDirectionMeta(direction) {
  if (direction === 'debit') {
    return {
      label: 'Debit',
      icon: 'solar:arrow-to-top-left-bold',
      accentColor: 'error.main',
      background: (theme) => alpha(theme.palette.error.main, 0.08),
      border: (theme) => alpha(theme.palette.error.main, 0.14),
    };
  }

  return {
    label: 'Credit',
    icon: 'solar:arrow-to-bottom-right-bold',
    accentColor: 'success.main',
    background: (theme) => alpha(theme.palette.success.main, 0.08),
    border: (theme) => alpha(theme.palette.success.main, 0.14),
  };
}

function CircularLoaderPanel() {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, minHeight: 360 }}>
      <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
        <CircularProgress size={34} thickness={4} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading transactions...
        </Typography>
      </Stack>
    </Card>
  );
}

function CopyReferenceButton({ active, onClick, reference }) {
  return (
    <Tooltip title={active ? 'Copied' : 'Copy reference'}>
      <IconButton size="small" onClick={() => onClick(reference)} sx={{ p: 0.25, color: active ? 'primary.main' : 'text.secondary' }}>
        <Iconify icon={active ? 'solar:check-circle-bold' : 'solar:copy-bold'} width={15} height={15} />
      </IconButton>
    </Tooltip>
  );
}

CopyReferenceButton.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  reference: PropTypes.string.isRequired,
};

export default function UserTransactionsTab({ user }) {
  const { enqueueSnackbar } = useSnackbar();
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [transactions, setTransactions] = useState({
    data: [],
    current_page: 1,
    next_page: 0,
    total_pages: 0,
    total_size: 0,
    previous_page: null,
    page_size: 100,
  });
  const [state, setState] = useState({ loading: false, loaded: false, error: null });
  const [copiedReference, setCopiedReference] = useState('');
  const [drawer, setDrawerOpen] = useState({open: false, id: null, reference: null});

  const rows = useMemo(() => (transactions.data || []).map((row, index) => ({ id: row.id || row.reference || `${index}`, ...row })), [transactions.data]);
  const loadTransactions = useCallback(async (page = 1, field = 'created_at', order = 'desc') => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await axiosInstance.get(`${CRUD.user.transactions}?sort_field=${field}&sort_order=${order}&page=${page}&user_id=${user.user_id}`);
      setTransactions({ data: response.data?.data || [], current_page: response.data?.current_page || page, next_page: response.data?.next_page || 0, total_pages: response.data?.total_pages || 0, total_size: response.data?.total_size || 0, previous_page: response.data?.previous_page || null, page_size: response.data?.page_size || 100 });
      setState({ loading: false, loaded: true, error: null });
    } catch (error) {
      setState((prev) => ({ loading: false, loaded: prev.loaded, error: getErrorMessage(error) }));
    }
  }, [user.user_id]);

  useEffect(() => { loadTransactions(1, 'created_at', 'desc'); }, [loadTransactions]);
  

  const handleCopyReference = useCallback(
    async (reference) => {
      try {
        await navigator.clipboard.writeText(reference);
        setCopiedReference(reference);
        enqueueSnackbar('Transaction reference copied', { variant: 'success' });
        window.setTimeout(() => setCopiedReference((current) => (current === reference ? '' : current)), 1500);
      } catch (error) {
        enqueueSnackbar('Unable to copy transaction reference', { variant: 'error' });
      }
    },
    [enqueueSnackbar]
  );

  const columns = useMemo(() => [
    {
      field: 'transaction',
      headerName: 'Transaction',
      flex: 1.8,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const isCopied = copiedReference === row.reference;
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5, width: '100%' }}>
            
            <IconButton size="small" color="primary" onClick={() =>  setDrawerOpen({open:true, id: row.id, reference: row.reference}) }>
              <Iconify icon="ion:open-outline" width={22} height={22} />
            </IconButton>
            <Stack spacing={0.45} sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ lineHeight: 1.1 }}>{row.transaction_type || 'Transaction'}</Typography>
                <Label color={getStatusColor(row.status)}>{row.status || 'unknown'}</Label>
                {/* <Chip size="small" label={directionMeta.label} variant="outlined" sx={{ height: 22 }} /> */}
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>Ref: {row.reference}</Typography>
                <CopyReferenceButton active={isCopied} onClick={handleCopyReference} reference={row.reference} />
              </Stack>
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 170,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const directionMeta = getDirectionMeta(row.direction);
        const amount = formatWalletAmount(row.display_amount, row.display_currency);
        const sign = directionMeta.label === 'Debit' ? '-' : '+';
        return (
          <Stack spacing={0.1} alignItems="flex-end" sx={{ width: '100%' }}>
            <Typography variant="subtitle2" sx={{ color: directionMeta.accentColor, lineHeight: 1.1 }}>{sign}{amount}</Typography>
            <Typography variant="caption" color="text.secondary">{row.display_currency || row.currency || '—'}</Typography>
          </Stack>
        );
      },
    },
    {
      field: 'usd_amount',
      headerName: 'USD',
      width: 120,
      sortable: false,
      renderCell: (params) => <Typography variant="body2" color="text.secondary">{params.row.usd_amount || '0.00'}</Typography>,
    },
    {
      field: 'direction',
      headerName: 'Type',
      width: 120,
      sortable: false,
      // renderCell: (params) => <Typography variant="body2" color="text.secondary">{params.row.usd_amount || '0.00'}</Typography>,
    },

    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => <Typography variant="body2">{params.row.created_at ? fDateTime(params.row.created_at) : 'N/A'}</Typography>,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.4,
      minWidth: 260,
      sortable: false,
      renderCell: (params) => <Typography variant="body2" color="text.secondary" noWrap title={params.row.description || ''}>{params.row.description || 'No description'}</Typography>,
    },
  ], [copiedReference, handleCopyReference]);

  if (state.loading && !state.loaded) return <CircularLoaderPanel />;
  if (state.error && !state.loaded) return <SectionError message={state.error} onRetry={() => loadTransactions(transactions.current_page || 1, sortField, sortOrder)} />;

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 2.25 }}>
          <Stack spacing={2}>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="manage-user-transaction-sort-field">Sort field</InputLabel>
                <Select labelId="manage-user-transaction-sort-field" value={sortField} label="Sort field" onChange={(event) => setSortField(event.target.value)}>
                  <MenuItem value="amount">Amount</MenuItem>
                  <MenuItem value="created_at">Created at</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="manage-user-transaction-sort-order">Sort order</InputLabel>
                <Select labelId="manage-user-transaction-sort-order" value={sortOrder} label="Sort order" onChange={(event) => setSortOrder(event.target.value)}>
                  <MenuItem value="desc">Newest first</MenuItem>
                  <MenuItem value="asc">Oldest first</MenuItem>
                </Select>
              </FormControl>
              <LoadingButton variant="contained" loading={state.loading} onClick={() => loadTransactions(1, sortField, sortOrder)} startIcon={<Iconify icon="solar:refresh-bold" width={18} height={18} />} sx={{ whiteSpace: 'nowrap' }}>Apply</LoadingButton>
            </Stack>
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ p: 2.25 }}>
          {state.error && state.loaded ? <Box sx={{ mb: 2 }}><SectionError message={state.error} onRetry={() => loadTransactions(transactions.current_page || 1, sortField, sortOrder)} /></Box> : null}
          {!rows.length && !state.loading ? <EmptyModuleState title="No transactions" description="The transactions endpoint returned no rows for this user." icon="solar:card-transfer-bold" /> : null}
          {rows.length ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 460, md: 620 },
                  minHeight: 420,
                }}
              >
                <DataGrid
                  rows={rows}
                  columns={columns}
                  density="standard"
                  disableRowSelectionOnClick
                  disableColumnMenu
                  hideFooter
                  columnHeaderHeight={52}
                  rowHeight={72}
                  getRowClassName={(params) =>
                    `${params.row.direction === 'debit' ? 'row-debit' : 'row-credit'} status-${(params.row.status || 'default').toLowerCase()}`
                  }
                  sx={{
                    border: 0,
                    height: '100%',
                    width: '100%',
                    '& .MuiDataGrid-main': {
                      minHeight: 0,
                    },
                    // '& .MuiDataGrid-columnHeaders': {
                    //   bgcolor: 'background.neutral',
                    //   borderBottom: '1px solid',
                    //   borderColor: 'divider',
                    // },
                    // '& .MuiDataGrid-columnHeader': {
                    //   outline: 'none',
                    //   px: 1.5,
                    // },
                    // '& .MuiDataGrid-columnHeaderTitle': {
                    //   fontSize: 12,
                    //   fontWeight: 700,
                    //   color: 'text.secondary',
                    //   textTransform: 'uppercase',
                    //   letterSpacing: 0.7,
                    // },
                    '& .MuiDataGrid-cell': {
                      borderBottomColor: 'divider',
                      px: 1.5,
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      overflowX: 'auto',
                      overflowY: 'auto',
                    },
                    // '& .MuiDataGrid-row.row-debit': {
                    //   backgroundColor: (theme) => alpha(theme.palette.error.main, 0.02),
                    // },
                    // '& .MuiDataGrid-row.row-credit': {
                    //   backgroundColor: (theme) => alpha(theme.palette.success.main, 0.02),
                    // },
                    // '& .MuiDataGrid-row.status-pending': {
                    //   borderLeft: (theme) => `3px solid ${theme.palette.warning.main}`,
                    // },
                    // '& .MuiDataGrid-row.status-successful, & .MuiDataGrid-row.status-completed, & .MuiDataGrid-row.status-verified': {
                    //   borderLeft: (theme) => `3px solid ${theme.palette.success.main}`,
                    // },
                    // '& .MuiDataGrid-row.status-failed, & .MuiDataGrid-row.status-rejected, & .MuiDataGrid-row.status-cancelled': {
                    //   borderLeft: (theme) => `3px solid ${theme.palette.error.main}`,
                    // },
                    // '& .MuiDataGrid-row:hover': {
                    //   backgroundColor: 'action.hover',
                    // },
                  }}
                />
              </Box>
            </Paper>
          ) : null}
          {rows.length ? (
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary">Page {transactions.current_page || 1} of {transactions.total_pages || 1}</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" disabled={!transactions.previous_page || state.loading} onClick={() => loadTransactions(transactions.previous_page, sortField, sortOrder)}>Previous</Button>
                <Button variant="outlined" size="small" disabled={!transactions.next_page || state.loading} onClick={() => loadTransactions(transactions.next_page, sortField, sortOrder)}>Next</Button>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Card>

      <TransactionSideDrawer open={drawer.open} onClose={() => setDrawerOpen({drawer: false})} id={drawer.id} reference={drawer.reference} />
    </Stack>
  );
}

UserTransactionsTab.propTypes = { user: PropTypes.object.isRequired };
